import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Shield, Heart, Mail, ArrowLeft, Check, Sparkles } from "lucide-react";

export default function RefundPolicy() {
  useEffect(() => {
    document.title = "Refund Policy — All Sales Final | Mind Body Spirit HYGIENE™ Tools";

    const metaDesc = document.querySelector('meta[name="description"]');
    const desc =
      "Our refund policy: all sales are final. 'I GET TO' thinking begins with accountability. Here's the philosophy behind the policy.";
    if (metaDesc) {
      metaDesc.setAttribute("content", desc);
    }
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="refund-policy-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back home
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/build-your-own" className="text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium" data-testid="refund-nav-build-your-own">Build Your Own</Link>
            <Link to="/" className="text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium">
              Home
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#CCFBF1] text-[#25a1c2] rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-5">
            <Shield className="w-3.5 h-3.5" />
            Our Philosophy on Refunds
          </div>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight text-[#042F2E] mb-4 leading-[1.05]">
            All Sales Are <span className="text-[#25a1c2]">Final.</span>
          </h1>
          <p className="text-lg md:text-xl text-[#475569] max-w-2xl mx-auto leading-relaxed">
            And here's why that's a gift, not a policy.
          </p>
        </div>

        {/* Hero Statement Card */}
        <div className="bg-gradient-to-br from-[#068fa2] to-[#25a1c2] rounded-3xl p-8 md:p-10 text-white shadow-xl mb-10">
          <Sparkles className="w-10 h-10 text-yellow-300 mb-4" />
          <p className="font-serif text-xl md:text-2xl leading-relaxed italic mb-4">
            "All sales are final. <strong className="not-italic text-yellow-200">I GET TO</strong> thinking begins with accountability — including to yourself. By purchasing, you're choosing to invest in yourself, your family, and your growth. Thank you for trusting this work."
          </p>
          <p className="text-white/70 text-sm tracking-widest uppercase">— Temple Franklin</p>
        </div>

        {/* The Why */}
        <section className="mb-12" data-testid="why-section">
          <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E] mb-4">
            Why no refunds?
          </h2>
          <div className="prose prose-lg text-[#334155] space-y-4 leading-relaxed">
            <p>
              Every product in this shop — every printable, prompt pack, workbook, and ritual guide — is designed to help you <em>shift your thinking</em>. Not tomorrow. The moment you purchase.
            </p>
            <p>
              A refund policy is a beautiful thing in most businesses. But when the product IS the act of deciding — the act of saying <strong>"I GET TO invest in myself"</strong> — a refund button undermines the very transformation you bought.
            </p>
            <p>
              I'd rather give you 10% fewer sales and 100% more committed humans doing the work. That's what this policy protects. Yours and mine.
            </p>
          </div>
        </section>

        {/* What You DO Get */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E] mb-6">
            What you DO get with every purchase
          </h2>
          <div className="bg-white rounded-2xl border-2 border-[#CCFBF1] p-6 md:p-8 shadow-sm">
            <ul className="space-y-4">
              {[
                {
                  title: "Instant delivery — no shipping, no waiting",
                  body: "Your download appears on the confirmation screen the moment your payment goes through. Not in an hour. Not after a wait.",
                },
                {
                  title: "The file is yours to keep",
                  body: "Download it while you are on that screen — the download link stays open for 7 days. Once it is on your computer it is yours for good, to print as often as you like. And if you ever lose it, write to me and I will send it again. You will not be stuck.",
                },
                {
                  title: "Build Your Own also comes with a link that does not expire",
                  body: "The online half of that course lives at a web address you bookmark. No login, no password, and it will still be there next year.",
                },
                {
                  title: "Free future updates",
                  body: "If I refine, expand, or release a v2 of something you bought, it is yours at no extra cost. Write to me and I will send it.",
                },
                {
                  title: "A direct line to me",
                  body: "temple@mindbodyspirithygienetools.com — I read every note myself, and I answer.",
                },
                {
                  title: "Damaged or missing file? I fix it.",
                  body: "If your download is corrupted, broken, or never arrives, email me and I will make it right. Always. That is not a refund — that is fulfilling my end of the deal.",
                },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`benefit-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-[#25a1c2] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#042F2E] mb-0.5">{item.title}</p>
                    <p className="text-[#475569] text-sm leading-relaxed">{item.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* The Before-You-Buy Promise */}
        <section className="mb-12">
          <div className="bg-[#FFFBEB] border-2 border-[#FCD34D] rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3">
              <Heart className="w-5 h-5 text-[#92400E]" />
              <h3 className="font-serif text-xl text-[#78350F] font-bold">
                Before you buy.
              </h3>
            </div>
            <p className="text-[#78350F] leading-relaxed mb-4">
              We all wait for a better time. There is only this one.
            </p>
            <p className="text-[#78350F] leading-relaxed mb-4">
              You are here for a reason, and authentic change is what this is for. Now it's time for investing in you.
            </p>
            <p className="text-[#78350F] leading-relaxed">
              After the yes, make the commitment to yourself to take action and do the work. That commitment is why all sales are final. There is no one to hurry you along. You go at your own pace — just commit to doing it.
            </p>
          </div>
        </section>

        {/* The Exception */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E] mb-4">
            The one exception.
          </h2>
          <div className="prose prose-lg text-[#334155] leading-relaxed space-y-4">
            <p>
              If something goes wrong on my end — you are charged twice, or you pay and the file never arrives — write to me at{" "}
              <a
                href="mailto:temple@mindbodyspirithygienetools.com"
                className="text-[#25a1c2] font-bold underline"
                data-testid="contact-email"
              >
                temple@mindbodyspirithygienetools.com
              </a>{" "}
              and I will fix it, every time.
            </p>
            <p>
              That isn't about changing your mind. That's me keeping my side of the deal.
            </p>
          </div>
        </section>

        {/* Payment Processor Note */}
        <section className="mb-12">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 shadow-sm">
            <h3 className="font-serif text-lg text-[#042F2E] mb-3">
              Where you purchased matters.
            </h3>
            <ul className="text-[#475569] text-sm space-y-2 leading-relaxed">
              <li>
                <strong className="text-[#042F2E]">Purchased via our site (Stripe):</strong> This page is the full refund policy. All sales final.
              </li>
              <li>
                <strong className="text-[#042F2E]">Purchased via Gumroad:</strong> Same policy, enforced by Gumroad at checkout. "No refunds allowed" is clearly disclosed before you click buy.
              </li>
              <li>
                <strong className="text-[#042F2E]">Purchased via Teachers Pay Teachers:</strong> TPT has their own platform-wide refund rules which override ours. If you purchased there, contact TPT directly for their policy.
              </li>
            </ul>
          </div>
        </section>

        {/* Closing */}
        <div className="text-center bg-gradient-to-br from-[#068fa2] to-[#25a1c2] rounded-3xl p-8 md:p-10 text-white shadow-xl">
          <Sparkles className="w-10 h-10 text-yellow-300 mx-auto mb-4" />
          <h2 className="font-serif text-2xl md:text-3xl mb-4">
            You chose. That's the whole point.
          </h2>
          <p className="text-white/85 leading-relaxed max-w-lg mx-auto mb-6">
            Thank you for trusting me, this work, and the woman in the mirror who decided <em className="text-yellow-200 not-italic font-bold">she GETS TO</em> invest in herself today.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-white text-[#25a1c2] hover:bg-yellow-100 rounded-full px-6 py-3 font-bold text-sm transition-all hover:-translate-y-0.5 shadow-lg"
              data-testid="back-to-shop"
            >
              Back home
            </Link>
            <a
              href="mailto:temple@mindbodyspirithygienetools.com"
              className="inline-flex items-center gap-2 border-2 border-white/60 hover:border-white text-white rounded-full px-6 py-3 font-bold text-sm transition-all"
              data-testid="contact-cta"
            >
              <Mail className="w-4 h-4" /> Contact Temple
            </a>
          </div>
        </div>

        {/* Last updated */}
        <p className="text-center text-[#94A3B8] text-xs mt-10">
          Last updated: October 1, 2026 &middot; Mind Body Spirit HYGIENE™ Tools
        </p>
      </div>
    </div>
  );
}
